/**
 * Cache key utilities for CSV data caching
 * Provides consistent cache key generation and validation
 */

/**
 * Generate a cache key for CSV data
 * @param {string} dateStr - Date in YYYY-MM-DD format or date range in YYYY-MM-DD_YYYY-MM-DD format
 * @param {string|null} storeId - Store ID or null/undefined for no-store
 * @returns {string} Cache key
 */
export function generateCsvCacheKey(dateStr, storeId) {
  if (!dateStr || typeof dateStr !== 'string') {
    throw new Error('Date string is required for cache key generation');
  }
  
  // Validate date format (single date or date range)
  const singleDatePattern = /^\d{4}-\d{2}-\d{2}$/;
  const dateRangePattern = /^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/;
  
  if (!singleDatePattern.test(dateStr) && !dateRangePattern.test(dateStr)) {
    throw new Error('Date must be in YYYY-MM-DD format or YYYY-MM-DD_YYYY-MM-DD format for date ranges');
  }
  
  const normalizedStoreId = storeId && storeId !== 'no-store' ? storeId : 'no-store';
  return `csv-cache-${dateStr}-${normalizedStoreId}`;
}

/**
 * Parse a cache key back into its components
 * @param {string} cacheKey - Cache key to parse
 * @returns {{dateStr: string, storeId: string} | null} Parsed components or null if invalid
 */
export function parseCacheKey(cacheKey) {
  if (!cacheKey || typeof cacheKey !== 'string') {
    return null;
  }
  
  // Match both single date and date range patterns
  const singleDateMatch = cacheKey.match(/^csv-cache-(\d{4}-\d{2}-\d{2})-(.+)$/);
  const dateRangeMatch = cacheKey.match(/^csv-cache-(\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2})-(.+)$/);
  
  const match = dateRangeMatch || singleDateMatch;
  if (!match) {
    return null;
  }
  
  return {
    dateStr: match[1],
    storeId: match[2] === 'no-store' ? null : match[2]
  };
}

/**
 * Check if a key is a valid CSV cache key
 * @param {string} key - Key to validate
 * @returns {boolean} True if valid CSV cache key
 */
export function isCsvCacheKey(key) {
  return parseCacheKey(key) !== null;
}

/**
 * Get all CSV cache keys from localStorage
 * @returns {string[]} Array of CSV cache keys
 */
export function getAllCsvCacheKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isCsvCacheKey(key)) {
      keys.push(key);
    }
  }
  return keys;
}
